import React, { ReactElement, SyntheticEvent, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { SlidePanel, FormInput, FormTextarea, FormSelect } from 'ui/patterns';
import { Button, Stack } from 'ui/components';

import {
  useGetLocationInfo,
  useSaveLocation,
  useGetStateProvince,
} from 'hooks';

import { showErrorToast } from 'utils/showToast';

import { AddEditLocationPageProps } from './editLocationTypes';
import { LocationInput } from 'types/locations';
import { ErrorModal } from 'types/modalTypes';

const formDefaults = {
  City: '',
  Country: '',
  County: '',
  Name: '',
  Notes: '',
  PostalCode: '',
  StateProvinceId: 0,
  Street: '',
};

export default function EditLocation({
  showPanel,
  togglePanel,
  accountId,
  locationId,
  mode,
}: AddEditLocationPageProps): ReactElement {
  const { data: selectedLocation } = useGetLocationInfo({
    account: accountId,
    id: locationId,
  });
  const { data: stateProvince } = useGetStateProvince();
  const { mutate: saveLocation, isLoading: isSaveLocationLoading } =
    useSaveLocation();

  const { register, handleSubmit, reset, control } = useForm<
    Partial<LocationInput>
  >({
    defaultValues: { ...formDefaults },
  });

  useEffect(() => {
    if (selectedLocation && !isSaveLocationLoading) {
      reset({ ...selectedLocation.Data });
    }
  }, [selectedLocation, reset, isSaveLocationLoading]);

  function resetForm() {
    reset(formDefaults);
  }

  const addLocationActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          resetForm();
          togglePanel();
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSubmit(onSubmit)}
        type="submit"
        variant="primary"
        disabled={isSaveLocationLoading ? true : false}
      >
        Save
      </Button>
    </>
  );

  function onSubmit(data: any) {
    saveLocation(
      {
        ...data,
        AccountId: accountId,
        Id: selectedLocation?.Data?.Id || 0,
      },
      {
        onSuccess: () => {
          togglePanel();
          resetForm();
        },
        onError: (error: ErrorModal) => {
          showErrorToast({ message: error.Errors[0].Message });
        },
      },
    );
  }

  return (
    <>
      <SlidePanel
        title={`${mode === 'Edit' ? 'Edit Location' : 'Add New Location'}`}
        actions={addLocationActions()}
        onClose={() => {
          resetForm();
          togglePanel();
        }}
        open={showPanel}
      >
        <Stack direction="y" gap={4}>
          <FormInput register={register} label="Name" type="text" required />
          <FormInput register={register} label="Street" type="text" />
          <FormInput
            register={register}
            name="PostalCode"
            label="Zip Code"
            type="text"
          />
          <FormInput register={register} label="City" type="text" />
          <Controller
            render={(props: {
              onChange: (value: number) => void;
              value: number;
            }) => (
              <FormSelect
                value={props.value}
                label="State"
                options={stateProvince}
                onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                  props.onChange(parseInt(e.currentTarget.value, 10));
                }}
              />
            )}
            defaultValue={''}
            name="StateProvinceId"
            control={control}
          />
          <FormInput register={register} label="Country" type="text" />
          <FormInput register={register} label="County" type="text" />
          <FormTextarea register={register} label="Notes" name="Notes" />
        </Stack>
      </SlidePanel>
    </>
  );
}
